import React, { useState, useEffect } from 'react';
import { MdAdd, MdSave, MdDelete, MdEdit, MdScience, MdBiotech } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';
import YsaFieldListPanel from './YsaFieldListPanel';
import { buildEffectiveFields } from '../../utils/ysaFields';

export default function YsaTrainingTab({ companyId, config, sensors, refetchConfig }) {
    const { t } = useLanguage();
    const [activeCategoryId, setActiveCategoryId] = useState(null);
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [newFieldName, setNewFieldName] = useState('');
    const [addingCategory, setAddingCategory] = useState(false);
    const [addingGroup, setAddingGroup] = useState(false);
    const [addingField, setAddingField] = useState(false);
    const [renamingFieldId, setRenamingFieldId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [spektroValues, setSpektroValues] = useState({});
    const [deneyValues, setDeneyValues] = useState({});
    const [latestValues, setLatestValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [exportingSpektro, setExportingSpektro] = useState(false);
    const [exportingDeney, setExportingDeney] = useState(false);

    const categories = config.categories || [];
    const activeCategory = categories.find(c => c._id === activeCategoryId) || null;
    const groups = activeCategory?.groups || [];
    const activeGroup = groups.find(g => g._id === activeGroupId) || null;
    const effectiveFields = activeGroup ? buildEffectiveFields(sensors, activeGroup) : [];
    const manualFields = effectiveFields.filter(f => f.type === 'manual');

    useEffect(() => {
        setActiveGroupId(null);
    }, [activeCategoryId]);

    // Refetch every sensor's latest reading whenever the group changes, so both panels
    // show fresh values (sensors are always part of the field set, per group).
    useEffect(() => {
        setSpektroValues({});
        setDeneyValues({});
        if (!activeGroup || sensors.length === 0) {
            setLatestValues({});
            return;
        }
        api.post('/sensor-data/bulk/latest', { uuids: sensors.map(s => s.sensorUUID) })
            .then(res => {
                const map = {};
                (res.data.data || []).forEach(item => { map[item.sensorUUID] = item; });
                setLatestValues(map);
            })
            .catch(() => {});
    }, [activeGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddCategory = async () => {
        const trimmed = newCategoryName.trim();
        if (!trimmed) return;
        try {
            setAddingCategory(true);
            await api.post(`/ysa/config/${companyId}/categories`, { name: trimmed });
            setNewCategoryName('');
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        } finally {
            setAddingCategory(false);
        }
    };

    const handleDeleteCategory = async (category) => {
        if (!window.confirm(t('ysa.deleteCategoryConfirm', { name: category.name }))) return;
        try {
            await api.delete(`/ysa/config/${companyId}/categories/${category._id}`);
            if (activeCategoryId === category._id) setActiveCategoryId(null);
            toast.success(t('ysa.categoryDeleted'));
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        }
    };

    const handleAddGroup = async () => {
        const trimmed = newGroupName.trim();
        if (!trimmed || !activeCategoryId) return;
        try {
            setAddingGroup(true);
            await api.post(`/ysa/config/${companyId}/categories/${activeCategoryId}/groups`, { name: trimmed });
            setNewGroupName('');
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        } finally {
            setAddingGroup(false);
        }
    };

    const handleDeleteGroup = async (group) => {
        if (!window.confirm(t('ysa.deleteGroupConfirm', { name: group.name }))) return;
        try {
            await api.delete(`/ysa/config/${companyId}/categories/${activeCategoryId}/groups/${group._id}`);
            if (activeGroupId === group._id) setActiveGroupId(null);
            toast.success(t('ysa.groupDeleted'));
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        }
    };

    const handleAddField = async () => {
        const trimmed = newFieldName.trim();
        if (!trimmed || !activeGroupId) return;
        try {
            setAddingField(true);
            await api.post(`/ysa/config/${companyId}/categories/${activeCategoryId}/groups/${activeGroupId}/fields`, { name: trimmed });
            setNewFieldName('');
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        } finally {
            setAddingField(false);
        }
    };

    const handleRenameField = async (fieldId) => {
        if (!renameValue.trim()) return;
        try {
            await api.put(`/ysa/config/${companyId}/categories/${activeCategoryId}/groups/${activeGroupId}/fields/${fieldId}`, { name: renameValue.trim() });
            setRenamingFieldId(null);
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        }
    };

    const handleDeleteField = async (field) => {
        try {
            await api.delete(`/ysa/config/${companyId}/categories/${activeCategoryId}/groups/${activeGroupId}/fields/${field._id}`);
            setSpektroValues(v => { const c = { ...v }; delete c[field._id]; return c; });
            setDeneyValues(v => { const c = { ...v }; delete c[field._id]; return c; });
            await refetchConfig();
        } catch (err) {
            toast.error(err.response?.data?.message);
        }
    };

    const handleSave = async () => {
        if (!activeGroup) return;
        const missing = manualFields.some(f =>
            spektroValues[f._id] === undefined || spektroValues[f._id] === '' || Number.isNaN(Number(spektroValues[f._id])) ||
            deneyValues[f._id] === undefined || deneyValues[f._id] === '' || Number.isNaN(Number(deneyValues[f._id]))
        );
        if (missing) {
            toast.error(t('ysa.fillAllFieldsError'));
            return;
        }
        try {
            setSaving(true);
            await api.post(`/ysa/company/${companyId}/samples`, {
                categoryId: activeCategoryId,
                groupId: activeGroupId,
                spektroManual: manualFields.map(f => ({ fieldId: f._id, manualValue: Number(spektroValues[f._id]) })),
                deneyManual: manualFields.map(f => ({ fieldId: f._id, manualValue: Number(deneyValues[f._id]) }))
            });
            toast.success(t('ysa.sampleSaved'));
            setSpektroValues({});
            setDeneyValues({});
        } catch (err) {
            toast.error(err.response?.data?.message || t('ysa.sampleSaveFailed'));
        } finally {
            setSaving(false);
        }
    };

    // Each panel (Spektrofotometre / Deney) has its own export button and downloads
    // only its own data — they're independently entered/analyzed, so never combined.
    const handleExportPanel = async (panel) => {
        if (!activeGroupId) {
            toast.error(t('ysa.selectGroupFirst'));
            return;
        }
        const setExporting = panel === 'spektro' ? setExportingSpektro : setExportingDeney;
        try {
            setExporting(true);
            const response = await api.get(
                `/ysa/company/${companyId}/samples/export/excel?groupId=${activeGroupId}&panel=${panel}`,
                { responseType: 'blob' }
            );
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            const groupSlug = (activeGroup?.name || 'grup').trim().toLowerCase()
                .replace(/[^a-z0-9ğüşöçı]+/gi, '-').replace(/^-+|-+$/g, '');
            link.setAttribute('download', `nortech-ysa-egitim-${panel}-${groupSlug}-${Date.now()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            toast.error(err.response?.data?.message || t('ysa.exportFailed'));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Category list */}
            <div className="card" style={{ padding: 20, width: 240, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>{t('ysa.categories')}</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <input
                        className="form-input form-input-sm"
                        placeholder={t('ysa.newCategoryPlaceholder')}
                        value={newCategoryName}
                        onChange={e => setNewCategoryName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); }}
                        style={{ flex: 1 }}
                    />
                    <button className="btn btn-primary btn-sm btn-icon" onClick={handleAddCategory} disabled={addingCategory || !newCategoryName.trim()}>
                        {addingCategory ? <span className="spinner spinner-sm" /> : <MdAdd />}
                    </button>
                </div>
                {categories.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('ysa.noCategories')}</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {categories.map(cat => (
                            <div key={cat._id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <button
                                    onClick={() => setActiveCategoryId(cat._id)}
                                    style={{
                                        flex: 1, textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                        background: activeCategoryId === cat._id ? 'rgba(79,142,247,0.12)' : 'transparent',
                                        color: activeCategoryId === cat._id ? 'var(--primary)' : 'var(--text-primary)',
                                        fontWeight: 600, fontSize: 13
                                    }}
                                >
                                    {cat.name}
                                </button>
                                <button className="btn btn-secondary btn-sm btn-icon" style={{ width: 26, height: 26 }} onClick={() => handleDeleteCategory(cat)} title={t('common.delete')}>
                                    <MdDelete size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Group list */}
            {activeCategory && (
                <div className="card" style={{ padding: 20, width: 240, flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>{t('ysa.groups')}</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <input
                            className="form-input form-input-sm"
                            placeholder={t('ysa.newGroupPlaceholder')}
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleAddGroup(); }}
                            style={{ flex: 1 }}
                        />
                        <button className="btn btn-primary btn-sm btn-icon" onClick={handleAddGroup} disabled={addingGroup || !newGroupName.trim()}>
                            {addingGroup ? <span className="spinner spinner-sm" /> : <MdAdd />}
                        </button>
                    </div>
                    {groups.length === 0 ? (
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('ysa.noGroups')}</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {groups.map(group => (
                                <div key={group._id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <button
                                        onClick={() => setActiveGroupId(group._id)}
                                        style={{
                                            flex: 1, textAlign: 'left', padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                            background: activeGroupId === group._id ? 'rgba(79,142,247,0.12)' : 'transparent',
                                            color: activeGroupId === group._id ? 'var(--primary)' : 'var(--text-primary)',
                                            fontWeight: 600, fontSize: 13
                                        }}
                                    >
                                        {group.name}
                                    </button>
                                    <button className="btn btn-secondary btn-sm btn-icon" style={{ width: 26, height: 26 }} onClick={() => handleDeleteGroup(group)} title={t('common.delete')}>
                                        <MdDelete size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Group detail: manual field management + the two mirrored panels */}
            {activeGroup && (
                <div style={{ flex: 1, minWidth: 340, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className="card" style={{ padding: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t('ysa.manualFieldsTitle')}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                            {t('ysa.manualFieldsHint')}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: manualFields.length ? 14 : 0, flexWrap: 'wrap' }}>
                            <input
                                className="form-input form-input-sm"
                                placeholder={t('ysa.newFieldPlaceholder')}
                                value={newFieldName}
                                onChange={e => setNewFieldName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddField(); }}
                                style={{ flex: '1 1 200px' }}
                            />
                            <button className="btn btn-primary btn-sm" onClick={handleAddField} disabled={addingField || !newFieldName.trim()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {addingField ? <span className="spinner spinner-sm" /> : <MdAdd />} {t('ysa.addField')}
                            </button>
                        </div>
                        {manualFields.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {manualFields.map(f => (
                                    <div key={f._id} style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '6px 10px', borderRadius: 999, background: 'var(--bg-body)', border: '1px solid var(--border)'
                                    }}>
                                        {renamingFieldId === f._id ? (
                                            <input
                                                className="form-input form-input-sm"
                                                autoFocus
                                                value={renameValue}
                                                onChange={e => setRenameValue(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') handleRenameField(f._id); if (e.key === 'Escape') setRenamingFieldId(null); }}
                                                onBlur={() => handleRenameField(f._id)}
                                                style={{ width: 100 }}
                                            />
                                        ) : (
                                            <span style={{ fontSize: 12, fontWeight: 600 }}>{f.name}</span>
                                        )}
                                        <button
                                            onClick={() => { setRenamingFieldId(f._id); setRenameValue(f.name); }}
                                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}
                                        >
                                            <MdEdit size={13} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteField(f)}
                                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger, #dc2626)', display: 'flex' }}
                                        >
                                            <MdDelete size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <YsaFieldListPanel
                            title={t('ysa.spektroTitle')}
                            icon={<MdScience />}
                            fields={effectiveFields}
                            latestValues={latestValues}
                            values={spektroValues}
                            onValueChange={(fid, val) => setSpektroValues(v => ({ ...v, [fid]: val }))}
                            onExport={() => handleExportPanel('spektro')}
                            exporting={exportingSpektro}
                        />
                        <YsaFieldListPanel
                            title={t('ysa.deneyTitle')}
                            icon={<MdBiotech />}
                            accent="var(--success)"
                            fields={effectiveFields}
                            latestValues={latestValues}
                            values={deneyValues}
                            onValueChange={(fid, val) => setDeneyValues(v => ({ ...v, [fid]: val }))}
                            onExport={() => handleExportPanel('deney')}
                            exporting={exportingDeney}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {saving ? <span className="spinner spinner-sm" /> : <MdSave />} {t('ysa.save')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
